import React from "react";
import moment from "moment-timezone";

import connectToStores from "alt-utils/lib/connectToStores";
import EventBucketStore from "../stores/EventBucketStore";
import EventBucketActions from "../actions/EventBucketActions";
import AnalyticsStore from "../stores/AnalyticsStore";
import AnalyticsActions from "../actions/AnalyticsActions";

import AnalyticsHeader from "./partials/analytics-header.jsx";
import WorkingIndicator from "./partials/working-indicator.jsx";
import RecurringPassAnalyticsTable from "./partials/recurring-pass-analytics-table.jsx";
import AnalyticsFilterWidget from "./partials/analytics-filter-widget.jsx";
import StatsBlockContainer from "./partials/stats-block-container.jsx";
import StatsBlock from "./partials/stats-block.jsx";

import cloneDeep from "lodash/cloneDeep";

class RecurringPassAnalytics extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      allTime: false,
      range: {
        key: "selection",
        startDate: moment().subtract(1, "month"),
        endDate: moment()
      },
      isMembership: null,
      filterState: "90 days" // default 90 days
    };

    this.onWindowClick = this.onWindowClick.bind(this);
    this.onWindowKeyDown = this.onWindowKeyDown.bind(this);
    this.handleSegmentFilterChange = this.handleSegmentFilterChange.bind(this);
    this.setPage = this.setPage.bind(this);
    this.onMembershipPage = this.onMembershipPage.bind(this);
  }

  static getStores() {
    return [EventBucketStore, AnalyticsStore];
  }

  static getPropsFromStores() {
    return {
      getEventBuckets: EventBucketStore.getEventBuckets,
      getEventBucketById: EventBucketStore.getEventBucketById,

      getPassSalesAnalytics: AnalyticsStore.getPassSalesAnalytics,

      // Note: getRecurringPassAnalytics collects recurring passes that are 
      // renewing or expiring.
      getRecurringPassAnalytics: AnalyticsStore.getRecurringPassAnalytics,
      getExpiredPassAnalytics: AnalyticsStore.getExpiredPassAnalytics,
      getNewSubscriptionAnalytics: AnalyticsStore.getNewSubscriptionAnalytics,

      // Statbox info for Active Members and Failed Renewals
      getRecurringPassOverview: AnalyticsStore.getRecurringPassOverview,

      getIsFetchingRenewalAnalytics:
        AnalyticsStore.getIsFetchingRenewalAnalytics,
      getIsFetchingExpiredPassAnalytics:
        AnalyticsStore.getIsFetchingExpiredPassAnalytics,
      getIsFetchingNewSubscriptionAnalytics:
        AnalyticsStore.getIsFetchingNewSubscriptionAnalytics,
      getIsFetchingRecurringPassOverview:
        AnalyticsStore.getIsFetchingRecurringPassOverview,
    };
  }

  static contextTypes = {
    router: React.PropTypes.object
  };

  onWindowClick(e) {
    if (
      this.state.showRangeEditor &&
      (!this.container ||
        (!this.container.contains(e.target) &&
          !this.rangeButton.contains(e.target)))
    ) {
      this.setState({ showRangeEditor: false });
    }
  }

  onWindowKeyDown(e) {
    if (this.state.showRangeEditor && e.which === 27) {
      // esc
      this.setState({ showRangeEditor: false });
    }
  }

  onMembershipPage() {
    return this.props.location.pathname.includes("membership");
    // return this.props.route.name;
  }

  // Set the state of the page to reflect if we are on a 
  // pass page or a membership page.
  async setPage() {
    // reset date range filter when changing between pass and membership
    this.setState({ filterState: "90 days" });

    if (this.onMembershipPage()) {
      await this.setState({ isMembership: true })
      this.componentWillReceiveProps(this.props, "all");
    } else {
      await this.setState({ isMembership: false })
      this.componentWillReceiveProps(this.props, "all");
    }
  }

  componentDidMount() {
    this.setPage(); // set state to reflect pass vs. membership
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.setPage();  // set state to reflect pass vs. membership
    }
  }

  componentWillReceiveProps(newProps, force = null) {
    if (force === "all" || newProps.params.eventBucketId !== this.props.params.eventBucketId) {
      const eventBucketId = newProps.params.eventBucketId;

      // Break string into number and units of time
      const rangeArray = this.state.filterState.split(" ");
      const rangeAmount = Number(rangeArray[0]);
      const rangePeriod = rangeArray[1];

      AnalyticsActions.fetchRecurringPassAnalytics.defer({ // future
        eventBucketId,
        isMembership: this.state.isMembership,
        startDate: JSON.stringify(moment()),
        endDate: JSON.stringify(moment().add(rangeAmount, rangePeriod)),
        allTime: this.state.allTime,
      });
      AnalyticsActions.fetchExpiredPassAnalytics.defer({ // past
        eventBucketId,
        isMembership: this.state.isMembership,
        startDate: JSON.stringify(moment().subtract(rangeAmount, rangePeriod)),
        endDate: JSON.stringify(moment()),
        allTime: this.state.allTime,
      });
      AnalyticsActions.fetchNewSubscriptionAnalytics.defer({ // past
        eventBucketId,
        isMembership: this.state.isMembership,
        startDate: JSON.stringify(moment().subtract(rangeAmount, rangePeriod)),
        endDate: JSON.stringify(moment()),
        allTime: this.state.allTime,
      });
      AnalyticsActions.fetchRecurringPassOverview.defer({ // past
        eventBucketId,
        isMembership: this.state.isMembership,
        startDate: JSON.stringify(moment().subtract(rangeAmount, rangePeriod)),
        endDate: JSON.stringify(moment()),
        allTime: this.state.allTime,
      });

      EventBucketActions.fetchOne.defer(newProps.params.eventBucketId);
    }
  }

  async handleSegmentFilterChange(props) {
    this.setState({ filterState: props },
      () => this.componentWillReceiveProps(this.props, "all")
    );
  }

  render() {
    const { eventBucketId } = this.props.params;
    const eventBucket = this.props.getEventBucketById(eventBucketId);

    const recurringPassAnalytics = this.props.getRecurringPassAnalytics(eventBucketId);
    const expiredPassAnalytics = this.props.getExpiredPassAnalytics(eventBucketId);
    const newSubscriptionAnalytics = this.props.getNewSubscriptionAnalytics(eventBucketId);
    const recurringPassOverview = this.props.getRecurringPassOverview(eventBucketId);

    const isMembership = this.props.location.pathname.includes("membership");
    const defaultItemsPerPage = 25;

    // Break string into number and units of time
    const rangeArray = this.state.filterState.split(" ");
    const rangeAmount = Number(rangeArray[0]);
    const rangePeriod = rangeArray[1];

    if (
      !(
        recurringPassAnalytics &&
        expiredPassAnalytics &&
        newSubscriptionAnalytics &&
        recurringPassOverview
      ) ||
      !eventBucket
    ) {
      return <WorkingIndicator fullPage={true} />;
    }

    // Flatten data
    let processedRenewalData = [];
    for (let y = 0; y < recurringPassAnalytics.data.length; y++) {
      const thisPassSub = recurringPassAnalytics.data[y];
      for (let z = 0; z < thisPassSub.passes.length; z++) {
        let singlePassSub = cloneDeep(thisPassSub);
        singlePassSub.pass = thisPassSub.passes[z];
        processedRenewalData.push(singlePassSub);
      }
    }


    let processedExpiredData = [];
    for (let y = 0; y < expiredPassAnalytics.data.length; y++) {
      const thisPassSub = expiredPassAnalytics.data[y];
      for (let z = 0; z < thisPassSub.passes.length; z++) {
        let singlePassSub = cloneDeep(thisPassSub);
        singlePassSub.pass = thisPassSub.passes[z];
        processedExpiredData.push(singlePassSub);
      }
    }

    let processedNewSubscriptionData = [];
    for (let y = 0; y < newSubscriptionAnalytics.data.length; y++) {
      const thisPassSub = newSubscriptionAnalytics.data[y];
      for (let z = 0; z < thisPassSub.passes.length; z++) {
        let singlePassSub = cloneDeep(thisPassSub);
        singlePassSub.pass = thisPassSub.passes[z];
        processedNewSubscriptionData.push(singlePassSub);
      }
    }

    // Stat Blocks
    const statsActive = recurringPassOverview.data.activePasses ? recurringPassOverview.data.activePasses : 0;
    const statsNew = processedNewSubscriptionData.length;
    const statsRenewals = processedRenewalData.filter(r => {
      if (
        (moment(r.renewal_date) >= moment()) && (moment(r.renewal_date) <= moment().add(rangeAmount, rangePeriod))
        && (r.should_autorenew === true)
      ) {
        return true
      }
    }).length;

    const statsExpiring = processedRenewalData.filter(r => {
      if (
        (moment(r.renewal_date) >= moment()) && (moment(r.renewal_date) <= moment().add(rangeAmount, rangePeriod))
        && (r.should_autorenew === false)
      ) {
        return true
      }
    }).length;
    const statsFailedRenewals = recurringPassOverview.data.failedRenewals ? recurringPassOverview.data.failedRenewals : 0;

    const browserTimezone = moment.tz(moment.tz.guess()).format('z');

    return (
      <div>
        <AnalyticsHeader
          eventBucket={eventBucket}
          allEventBuckets={this.props.getEventBuckets()}
          label={isMembership ? "membership status analytics" : "recurring pass analytics"}
          subpath="recurring_pass"
        />

        <AnalyticsFilterWidget
          key={isMembership ? "membership" : "passes"}
          showSegmentFilter={true}
          onSegmentFilterChange={this.handleSegmentFilterChange}
          segmentFilterTitle={`Choose date range (${browserTimezone})`}
          segmentFilterSelected={this.state.filterState}
          segmentFilterConfig={[
            {
              value: "14 days",
              label: "14 Days"
            },
            {
              value: "30 days",
              label: "30 Days"
            },
            {
              value: "60 days",
              label: "60 Days"
            },
            {
              value: "90 days",
              label: "90 Days"
            },
            {
              value: "1 year",
              label: "1 year"
            },
            {
              value: "2 years",
              label: "2 years"
            },
            {
              value: "3 years",
              label: "3 years"
            },
          ]}
        />

        {
          this.props.getIsFetchingRenewalAnalytics()
            || this.props.getIsFetchingExpiredPassAnalytics()
            || this.props.getIsFetchingNewSubscriptionAnalytics()
            || this.props.getIsFetchingRecurringPassOverview()
            ? (
              <div style={{ width: 100, height: 100, marginTop: 20 }}>
                <WorkingIndicator fullPage={true} />
              </div>
            ) : (
              <div className="analytics-element">
                <div className="statbox-label">in the past {this.state.filterState}</div>
                <StatsBlockContainer>
                  <StatsBlock
                    value={statsActive}
                    label={`Active ${isMembership ? "Members" : "Passholders"} as of today`}
                  />
                  <StatsBlock
                    value={statsNew}
                    label={`New ${isMembership ? "Members" : "Passholders"} in the past ${this.state.filterState}`}
                  />
                  <StatsBlock
                    value={statsRenewals}
                    label={`${isMembership ? "membership" : "pass"} Renewals in the next ${this.state.filterState}`}
                  />
                  <StatsBlock
                    value={statsExpiring}
                    label={`${isMembership ? "membership" : "pass"} Expiring in the next ${this.state.filterState}`}
                  />
                  <StatsBlock
                    value={statsFailedRenewals}
                    label={`Failed ${isMembership ? "membership" : "pass"} Renewals in the past ${this.state.filterState}`}
                  />
                </StatsBlockContainer>
              </div>
            )
        }

        {
          this.props.getIsFetchingRenewalAnalytics() ? (
            <div style={{ width: 100, height: 100, marginTop: 20 }}>
              <WorkingIndicator fullPage={true} />
            </div>
          ) : (
            <div className="analytics-element">
              <h2>Recurring {isMembership ? "memberships" : "passes"} renewing or expiring in the next {this.state.filterState}</h2>

              <RecurringPassAnalyticsTable
                reportName={`Renewing or Expiring ${isMembership ? "Memberships" : "Passes"}`}
                eventBucket={eventBucket}
                data={processedRenewalData}
                includeAutorenew={true}
                includeCardOnFile={true}
                includeExpiredDate={false}
                includeRenewalDate={true}
                isMembership={isMembership}
                itemsPerPage={defaultItemsPerPage}
              />
            </div>
          )
        }
        {
          this.props.getIsFetchingExpiredPassAnalytics() ? (
            <div style={{ width: 100, height: 100, marginTop: 20 }}>
              <WorkingIndicator />
            </div>
          ) : (
            <div className="analytics-element">
              <h2>Expired {isMembership ? "memberships" : "passes"} in the past {this.state.filterState}</h2>
              <RecurringPassAnalyticsTable
                reportName={`Expired ${isMembership ? "Memberships" : "Passes"}`}
                eventBucket={eventBucket}
                data={processedExpiredData}
                includeAutorenew={false}
                includeCardOnFile={false}
                includeExpiredDate={true}
                includeRenewalDate={false}
                isMembership={isMembership}
                itemsPerPage={defaultItemsPerPage}
              />
            </div>
          )
        }
        {
          this.props.getIsFetchingNewSubscriptionAnalytics() ? (
            <div style={{ width: 100, height: 100, marginTop: 20 }}>
              <WorkingIndicator />
            </div>
          ) : (
            <div className="analytics-element">
              <h2>New subscriptions in the past {this.state.filterState}</h2>
              <RecurringPassAnalyticsTable
                reportName="New Subscriptions"
                eventBucket={eventBucket}
                data={processedNewSubscriptionData}
                includeAutorenew={true}
                includeCardOnFile={true}
                includeExpiredDate={false}
                includeRenewalDate={true}
                isMembership={isMembership}
                itemsPerPage={defaultItemsPerPage}
              />
            </div>
          )
        }
      </div >
    );
  }
}

export default connectToStores(RecurringPassAnalytics);



// WEBPACK FOOTER //
// ./src/app/components/recurring-pass-analytics.jsx